import React, { useEffect, useMemo, useState } from "react";
import { api, fmtINR, fmtNum, todayISO, fmtDate } from "../lib/api";
import { PartyPopper, Trash2, Pencil, MessageCircle, Printer, X } from "lucide-react";
import { toast } from "sonner";
import BillShareModal from "../components/BillShareModal";
import { openBookingWindow, shareBookingWhatsApp } from "../lib/bill";

const CAT_LABEL = { chicken: "Chicken", mutton: "Mutton", offals: "Offals" };

export default function PartyOrders() {
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState({});
  const [orders, setOrders] = useState([]);
  const [partyCustomers, setPartyCustomers] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  // Order form
  const [custName, setCustName] = useState("");
  const [custPhone, setCustPhone] = useState("");
  const [orderDate, setOrderDate] = useState(todayISO());
  const [deliveryDate, setDeliveryDate] = useState("");
  const [paidAmt, setPaidAmt] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState({}); // product_id -> {rate, qty}
  const [editingId, setEditingId] = useState(null);
  const [editingNo, setEditingNo] = useState(null);

  // Share modal
  const [lastOrder, setLastOrder] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    const [pRes, prRes, oRes, cRes, uRes] = await Promise.all([
      api.get("/products?active_only=true"),
      api.get(`/daily-prices?date=${todayISO()}&channel=retail`),
      api.get("/party-orders?limit=100"),
      api.get("/party-orders/customers"),
      api.get("/party-orders/upcoming?days=7"),
    ]);
    setProducts(pRes.data);
    const map = {};
    prRes.data.forEach((p) => (map[p.product_id] = p.price_per_kg));
    setPrices(map);
    setOrders(oRes.data);
    setPartyCustomers(cRes.data);
    setUpcoming(uRes.data || []);
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const out = {};
    products.forEach((p) => {
      if (!out[p.category]) out[p.category] = [];
      out[p.category].push(p);
    });
    return out;
  }, [products]);

  const lineOf = (p) => {
    const ln = lines[p.id] || {};
    return {
      rate: ln.rate ?? (prices[p.id] ?? ""),
      qty: ln.qty ?? "",
    };
  };

  const updateLine = (p, field, val) => {
    setLines((l) => {
      const cur = l[p.id] || { rate: prices[p.id] ?? "", qty: "" };
      return { ...l, [p.id]: { ...cur, [field]: val } };
    });
  };

  const orderTotal = useMemo(() => {
    return Math.round(
      products.reduce((sum, p) => {
        const ln = lineOf(p);
        const rate = parseFloat(ln.rate) || 0;
        const qty = parseFloat(ln.qty) || 0;
        return sum + rate * qty;
      }, 0)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, products, prices]);

  const resetForm = () => {
    setCustName("");
    setCustPhone("");
    setOrderDate(todayISO());
    setDeliveryDate("");
    setPaidAmt("");
    setNotes("");
    setLines({});
    setEditingId(null);
    setEditingNo(null);
  };

  const buildItems = () =>
    products
      .map((p) => {
        const ln = lineOf(p);
        const rate = parseFloat(ln.rate) || 0;
        const qty = parseFloat(ln.qty) || 0;
        if (!qty) return null;
        return {
          product_id: p.id,
          product_name: p.name,
          category: p.category,
          unit: p.unit || "kg",
          kg: qty,
          rate,
          amount: Math.round(rate * qty),
        };
      })
      .filter(Boolean);

  const saveOrder = async () => {
    if (!custName.trim()) {
      toast.error("Enter customer name");
      return;
    }
    const items = buildItems();
    if (items.length === 0) {
      toast.error("Add at least one product with quantity");
      return;
    }
    const payload = {
      date: orderDate,
      delivery_date: deliveryDate,
      customer_name: custName.trim(),
      phone: custPhone.trim(),
      items,
      paid_amount: parseFloat(paidAmt) || 0,
      notes,
    };
    try {
      let res;
      if (editingId) {
        res = await api.put(`/party-orders/${editingId}`, payload);
        toast.success(`Order #${res.data.order_no} updated`);
      } else {
        res = await api.post("/party-orders", payload);
        toast.success(
          res.data.rate_pending
            ? `Booking #${res.data.order_no} saved · Advance ${fmtINR(res.data.paid_amount)}`
            : `Party order #${res.data.order_no} saved · ${fmtINR(res.data.total_amount)}`
        );
      }
      setLastOrder(res.data);
      if (res.data.rate_pending) {
        openBookingWindow(res.data);
      } else {
        setShareOpen(true);
      }
      resetForm();
      load();
    } catch (e) {
      toast.error("Failed to save order");
    }
  };

  const editOrder = (o) => {
    setEditingId(o.id);
    setEditingNo(o.order_no);
    setCustName(o.customer_name);
    setCustPhone(o.phone || "");
    setOrderDate(o.date);
    setDeliveryDate(o.delivery_date || "");
    setPaidAmt(o.paid_amount ? String(o.paid_amount) : "");
    setNotes(o.notes || "");
    const ln = {};
    o.items.forEach((it) => {
      ln[it.product_id] = { rate: String(it.rate), qty: String(it.kg) };
    });
    setLines(ln);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteOrder = async (o) => {
    if (!window.confirm(`Delete party order #${o.order_no}?`)) return;
    await api.delete(`/party-orders/${o.id}`);
    toast.success("Order deleted");
    if (editingId === o.id) resetForm();
    load();
  };

  const toSaleLike = (o) => ({
    ...o,
    channel: "wholesale",
    is_party: true,
    bill_no: o.order_no,
    payment_mode: o.payment_status,
  });

  const selectPartyCustomer = (name) => {
    setCustName(name);
    const pc = partyCustomers.find((c) => c.name === name);
    if (pc?.phone) setCustPhone(pc.phone);
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto page-enter" data-testid="party-orders-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
          <PartyPopper className="h-3 w-3" /> Event / Function Orders
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
          Party Orders
        </h1>
      </div>

      {/* Upcoming delivery reminders */}
      {upcoming.length > 0 && (
        <div className="mb-6 border border-amber-300 bg-amber-50 p-3 flex flex-wrap gap-2 items-center" data-testid="party-delivery-reminders">
          <span className="text-[10px] uppercase tracking-[0.2em] text-amber-900 font-bold">
            🚚 Upcoming Deliveries
          </span>
          {upcoming.map((o) => (
            <span
              key={o.id}
              data-testid={`delivery-reminder-${o.id}`}
              className={`text-xs px-2 py-1 ${
                o.delivery_date === todayISO()
                  ? "bg-rose-600 text-white"
                  : "bg-amber-200 text-amber-900"
              }`}
            >
              #{o.order_no} {o.customer_name} — {o.delivery_date === todayISO() ? "TODAY" : fmtDate(o.delivery_date)} · {fmtINR(o.total_amount)}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Order form */}
        <div className="xl:col-span-3">
          <div className="border border-zinc-200 bg-white p-5" data-testid="party-order-form">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {editingId ? `Editing Order #${editingNo}` : "New Party Order"}
              </div>
              {editingId && (
                <button
                  onClick={resetForm}
                  data-testid="party-cancel-edit"
                  className="text-xs uppercase tracking-wider text-rose-600 hover:underline flex items-center gap-1"
                >
                  <X className="h-3 w-3" /> Cancel Edit
                </button>
              )}
            </div>

            {!editingId && orders.some((o) => o.rate_pending) && (
              <div className="mb-4 border border-amber-300 bg-amber-50 p-3">
                <label className="text-[10px] uppercase tracking-wider text-amber-900 font-bold">
                  Finalize a Booking — select order to fill rates &amp; generate final bill
                </label>
                <select
                  value=""
                  onChange={(e) => {
                    const o = orders.find((x) => x.id === e.target.value);
                    if (o) {
                      editOrder(o);
                      toast.info(`Booking #${o.order_no} loaded — enter rates, then Update to generate the final bill (advance already recorded)`);
                    }
                  }}
                  data-testid="finalize-booking-select"
                  className="w-full border border-amber-300 bg-white px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Select booking order…</option>
                  {orders
                    .filter((o) => o.rate_pending)
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        #{o.order_no} · {o.customer_name} · {fmtDate(o.date)} · Adv {fmtINR(o.paid_amount)}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 mb-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Customer Name</label>
                <input
                  list="party-customer-list"
                  value={custName}
                  onChange={(e) => selectPartyCustomer(e.target.value)}
                  placeholder="e.g. Rahman Function Hall"
                  data-testid="party-cust-name"
                  className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-sky-600"
                />
                <datalist id="party-customer-list">
                  {partyCustomers.map((c) => (
                    <option key={c.name} value={c.name} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Phone (WhatsApp)</label>
                <input
                  value={custPhone}
                  onChange={(e) => setCustPhone(e.target.value)}
                  placeholder="e.g. 9198765..."
                  data-testid="party-cust-phone"
                  className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 font-mono-num focus:outline-none focus:ring-2 focus:ring-sky-600"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Order Date</label>
                <input
                  type="date"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                  data-testid="party-order-date"
                  className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-sky-600"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Delivery Date</label>
                <input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  data-testid="party-delivery-date"
                  className="w-full border border-amber-300 bg-amber-50 px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>

            {/* Products with adjustable rates */}
            {Object.keys(grouped).map((cat) => (
              <div key={cat} className="mb-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50 border border-zinc-200 px-3 py-1.5">
                  {CAT_LABEL[cat] || cat}
                </div>
                <div className="border border-t-0 border-zinc-200 divide-y divide-zinc-100">
                  {grouped[cat].map((p) => {
                    const ln = lineOf(p);
                    const amt = Math.round((parseFloat(ln.rate) || 0) * (parseFloat(ln.qty) || 0));
                    return (
                      <div key={p.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2" data-testid={`party-line-${p.id}`}>
                        <div className="w-full sm:w-auto sm:flex-1 min-w-0 text-sm truncate">{p.name}</div>
                        <div className="flex items-center gap-1 ml-auto sm:ml-0">
                          <span className="text-[10px] text-zinc-400">₹</span>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="Rate"
                            value={ln.rate}
                            onChange={(e) => updateLine(p, "rate", e.target.value)}
                            data-testid={`party-rate-${p.id}`}
                            className="w-16 sm:w-20 border border-zinc-300 px-1.5 sm:px-2 py-1.5 text-sm font-mono-num text-right focus:outline-none focus:ring-2 focus:ring-sky-600"
                          />
                        </div>
                        <input
                          type="number"
                          step={p.unit === "piece" ? "1" : "0.001"}
                          placeholder={p.unit === "piece" ? "Pcs" : "Kg"}
                          value={ln.qty}
                          onChange={(e) => updateLine(p, "qty", e.target.value)}
                          data-testid={`party-qty-${p.id}`}
                          className="w-16 sm:w-20 border border-zinc-300 px-1.5 sm:px-2 py-1.5 text-sm font-mono-num text-right focus:outline-none focus:ring-2 focus:ring-sky-600"
                        />
                        <div className="w-16 sm:w-20 text-right font-mono-num text-sm font-semibold">
                          {amt > 0 ? fmtINR(amt) : "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="bg-zinc-50 border border-zinc-200 px-4 py-3 mb-3">
              <div className="flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-wider text-zinc-500">Order Total</span>
                <span className="font-display text-3xl font-bold font-mono-num" data-testid="party-order-total">
                  {fmtINR(orderTotal)}
                </span>
              </div>
              {products.some((p) => {
                const ln = lineOf(p);
                return (parseFloat(ln.qty) || 0) > 0 && !(parseFloat(ln.rate) || 0);
              }) && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 mt-2" data-testid="party-booking-note">
                  ⚠ Rate not set for some items — this will be saved as an <b>Advance Booking</b>. A booking receipt (product + ordered qty only) will be generated. Fill rates later to make the final bill.
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
              <input
                type="number"
                placeholder="Advance amount (₹, 0 = full credit)"
                value={paidAmt}
                onChange={(e) => setPaidAmt(e.target.value)}
                data-testid="party-paid-input"
                className="border border-zinc-300 px-3 py-2 text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-sky-600"
              />
              <input
                placeholder="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="party-notes-input"
                className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
              />
            </div>

            <button
              onClick={saveOrder}
              data-testid="party-save-btn"
              className="w-full bg-sky-600 text-white py-3.5 font-display font-bold text-base uppercase tracking-wider hover:bg-sky-700 active:scale-[0.98] transition-all"
            >
              {editingId ? `Update Order #${editingNo}` : `Save Party Order · ${fmtINR(orderTotal)}`}
            </button>
          </div>
        </div>

        {/* Right column: orders list */}
        <div className="xl:col-span-2 space-y-6">
          {/* Recent orders */}
          <div className="border border-zinc-200 bg-white" data-testid="party-orders-list">
            <div className="px-5 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
              Recent Orders
            </div>
            {orders.length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-500">No party orders yet</div>
            ) : (
              orders.map((o) => (
                <div key={o.id} className="border-b border-zinc-100 px-4 py-3" data-testid={`party-order-${o.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">
                        #{o.order_no} · {o.customer_name}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {fmtDate(o.date)}
                        {o.delivery_date ? ` · 🚚 ${fmtDate(o.delivery_date)}` : ""} · {o.items.length} items · {fmtNum(o.total_kg, 2)} kg
                        {o.paid_amount > 0 && (
                          <span className="ml-1 text-emerald-700">
                            Adv {fmtINR(o.paid_amount)} · Bal {fmtINR(Math.max(0, o.total_amount - o.paid_amount))}
                          </span>
                        )}
                        <span
                          className={`ml-2 px-1.5 py-0.5 text-[10px] uppercase ${
                            o.payment_status === "paid"
                              ? "bg-emerald-50 text-emerald-700"
                              : o.payment_status === "partial"
                              ? "bg-amber-50 text-amber-700"
                              : "bg-rose-50 text-rose-700"
                          }`}
                        >
                          {o.payment_status}
                        </span>
                        {o.rate_pending && (
                          <span className="ml-1 px-1.5 py-0.5 text-[10px] uppercase bg-amber-100 text-amber-800 font-bold" data-testid={`party-booking-badge-${o.id}`}>
                            Booking
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="font-mono-num font-bold text-sm shrink-0">
                      {o.rate_pending ? `Adv ${fmtINR(o.paid_amount)}` : fmtINR(o.total_amount)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    {o.rate_pending ? (
                      <>
                        <button
                          onClick={() => shareBookingWhatsApp(o, { phone: o.phone })}
                          data-testid={`party-booking-wa-${o.id}`}
                          className="flex-1 border border-amber-600 text-amber-800 px-2 py-1.5 text-[10px] uppercase tracking-wider hover:bg-amber-50 flex items-center justify-center gap-1"
                        >
                          <MessageCircle className="h-3 w-3" /> Booking Receipt
                        </button>
                        <button
                          onClick={() => openBookingWindow(o)}
                          data-testid={`party-booking-print-${o.id}`}
                          className="border border-zinc-300 px-2 py-1.5 text-zinc-500 hover:bg-zinc-100"
                        >
                          <Printer className="h-3 w-3" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          setLastOrder(o);
                          setShareOpen(true);
                        }}
                        data-testid={`party-share-${o.id}`}
                        className="flex-1 border border-emerald-600 text-emerald-700 px-2 py-1.5 text-[10px] uppercase tracking-wider hover:bg-emerald-50 flex items-center justify-center gap-1"
                      >
                        <MessageCircle className="h-3 w-3" /> Invoice
                      </button>
                    )}
                    <button
                      onClick={() => editOrder(o)}
                      data-testid={`party-edit-${o.id}`}
                      className="flex-1 border border-zinc-300 px-2 py-1.5 text-[10px] uppercase tracking-wider hover:bg-zinc-100 flex items-center justify-center gap-1"
                    >
                      <Pencil className="h-3 w-3" /> {o.rate_pending ? "Finalize" : "Edit"}
                    </button>
                    <button
                      onClick={() => deleteOrder(o)}
                      data-testid={`party-delete-${o.id}`}
                      className="border border-zinc-300 px-2 py-1.5 text-zinc-400 hover:text-rose-600 hover:border-rose-300"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <BillShareModal
        key={lastOrder?.id || "none"}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        sale={lastOrder ? toSaleLike(lastOrder) : null}
        defaultPhone={lastOrder?.phone || ""}
      />
    </div>
  );
}
